/**
 * ReferralQueueNotice (KAN-724): a queued message by referral on the client page.
 *
 * Owner only. When a "telegram.referral" job of this client waits in the queue,
 * shows from which account and when it goes, with "Отправить сейчас": the
 * gateway planner then sends it on its next tick outside the sending window
 * (account state and the pause between messages still apply).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/services/api/client';
import { clientReferralApi, clientReferralKey } from '@/services/api/clientReferral';

interface ReferralQueueNoticeProps {
  clientId: string;
}

function formatRunAt(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errorDetail(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  return detail || 'Не удалось поставить сообщение на отправку';
}

function useIsOwner(): boolean {
  const { data } = useQuery<{ role?: string }>({
    queryKey: ['currentUser'],
    queryFn: async () => (await apiClient.get('/api/v1/auth/me/')).data,
    staleTime: 5 * 60 * 1000,
  });
  return data?.role === 'OWNER';
}

export function ReferralQueueNotice({ clientId }: ReferralQueueNoticeProps): JSX.Element | null {
  const isOwner = useIsOwner();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: clientReferralKey(clientId),
    queryFn: () => clientReferralApi.getStatus(clientId),
    enabled: isOwner,
  });
  const sendNow = useMutation({
    mutationFn: () => clientReferralApi.sendNow(clientId),
    onSuccess: (result) => {
      toast.success(`Сообщение поставлено на отправку с аккаунта ${result.account_label}`);
      void queryClient.invalidateQueries({ queryKey: clientReferralKey(clientId) });
    },
    onError: (error) => toast.error(errorDetail(error)),
  });

  if (!isOwner || !data?.pending) return null;
  const runAt = formatRunAt(data.run_at);
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
      data-testid="referral-queue-notice"
    >
      <span className="min-w-0 flex-1" title={data.text_preview ?? undefined}>
        В очереди сообщение по рекомендации с аккаунта {data.account_label}
        {runAt ? `, уйдёт ${runAt}` : ''}.
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        disabled={sendNow.isPending}
        onClick={() => sendNow.mutate()}
      >
        {sendNow.isPending ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        ) : (
          <Send className="mr-1 h-3 w-3" />
        )}
        Отправить сейчас
      </Button>
    </div>
  );
}
